import {useEffect, useRef} from 'react';
import {getFocusableTreeWalker} from '@sail/react-aria';

type Entry = [ref: React.RefObject<HTMLElement>, hidden: boolean];

function isElement(node: Node | null): node is Element {
  return node instanceof Element;
}

export function useUntabbableDescendants(entries: Entry[]) {
  const originalTabIndices = useRef<Map<Element, string | null>>(new Map());

  const hiddenValues = entries.map(([, hidden]) => hidden);

  useEffect(() => {
    const stored = originalTabIndices.current;

    for (const [ref, hidden] of entries) {
      const container = ref.current;
      if (!container) {
        // eslint-disable-next-line no-continue
        continue;
      }

      if (hidden) {
        const walker = getFocusableTreeWalker(container, {tabbable: true});
        let node = walker.nextNode();
        while (isElement(node)) {
          stored.set(node, node.getAttribute('tabindex'));
          node.setAttribute('tabindex', '-1');
          node = walker.nextNode();
        }
      } else {
        stored.forEach((original, el) => {
          if (container.contains(el)) {
            if (original === null) {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', original);
            }
            stored.delete(el);
          }
        });
      }
    }

    // entries is a new array each render;
    // we extract the hidden booleans as deps since refs are stable. Order must not change between renders.
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
  }, hiddenValues);
}
